/**
 * Sign-in against the Cognito hosted UI: authorization code flow with PKCE.
 *
 * Written out rather than pulled in as a dependency — it is about a hundred lines, and
 * Amplify would be a large library and a large surface for two users signing in to one
 * private tool.
 *
 * The **ID token** is what goes to the API, not the access token. Both are accepted by the
 * JWT authorizer, but only the ID token carries the `email` claim, and the email is what
 * the audit trail records against every change she makes.
 *
 * Token handling: the ID token lives in memory only. The refresh token goes to
 * localStorage, so closing the tab does not mean signing in again with MFA — the trade
 * every hosted-UI app makes, and an acceptable one for a private tool with two accounts on
 * a domain we control.
 */

import { config } from "./config";

const REFRESH_KEY = "rhs.refresh";
const VERIFIER_KEY = "rhs.verifier";
const STATE_KEY = "rhs.state";
/**
 * Where she was heading when the sign-in interrupted her.
 *
 * Cognito matches `redirect_uri` exactly, so the hash cannot ride along on the round trip.
 * sessionStorage can: the redirect comes back to the same tab, which is also why the PKCE
 * verifier above survives it. Without this, clicking "FCA lookup" on a stale session opens
 * a tab, asks her to sign in, and then shows her the review queue.
 */
const RETURN_KEY = "rhs.return";

/** Refresh this long before expiry, so a slow request never carries a dead token. */
const REFRESH_MARGIN_MS = 60_000;

interface TokenSet {
  idToken: string;
  expiresAt: number;
  email: string;
}

let current: TokenSet | null = null;
let inFlight: Promise<TokenSet | null> | null = null;

function base64url(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function randomString(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64url(bytes.buffer);
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(digest);
}

function decodeClaims(idToken: string): Record<string, unknown> {
  const payload = idToken.split(".")[1];
  if (!payload) return {};
  const padded = payload.replace(/-/g, "+").replace(/_/g, "/");
  try {
    return JSON.parse(decodeURIComponent(escape(atob(padded))));
  } catch {
    return {};
  }
}

function store(idToken: string, expiresIn: number, refreshToken?: string): TokenSet {
  const claims = decodeClaims(idToken);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  current = {
    idToken,
    expiresAt: Date.now() + expiresIn * 1000,
    email: String(claims.email ?? claims["cognito:username"] ?? "unknown"),
  };
  return current;
}

async function postToken(body: Record<string, string>): Promise<Response> {
  return fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: config.clientId, ...body }),
  });
}

/** Send the browser to the hosted UI. Returns a promise that never resolves — we navigate. */
export async function signIn(): Promise<never> {
  const verifier = randomString();
  const state = randomString();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  if (window.location.hash) sessionStorage.setItem(RETURN_KEY, window.location.hash);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: "email openid profile",
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: "S256",
  });
  window.location.assign(`${config.cognitoDomain}/oauth2/authorize?${params}`);
  return new Promise<never>(() => {});
}

export function signOut(): void {
  current = null;
  localStorage.removeItem(REFRESH_KEY);
  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.redirectUri,
  });
  window.location.assign(`${config.cognitoDomain}/logout?${params}`);
}

/** Exchange the ?code= Cognito redirected back with. Returns null if there is no code. */
async function completeSignIn(): Promise<TokenSet | null> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  if (!code) return null;

  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  // Clean the address bar before anything else, so a reload cannot replay the code.
  window.history.replaceState({}, "", url.pathname);

  if (!verifier || url.searchParams.get("state") !== expectedState) {
    throw new Error("Sign-in could not be verified. Please try again.");
  }

  const response = await postToken({
    grant_type: "authorization_code",
    code,
    redirect_uri: config.redirectUri,
    code_verifier: verifier,
  });
  if (!response.ok) {
    throw new Error(`Sign-in failed (${response.status}). Please try again.`);
  }
  const tokens = await response.json();

  // Only now is she actually signed in: put her back where she was going. Assigning the
  // hash fires `hashchange`, which is what the app listens on, so this needs no
  // cooperation from the caller. A sign-in that failed above leaves it untouched.
  const returnTo = sessionStorage.getItem(RETURN_KEY);
  sessionStorage.removeItem(RETURN_KEY);
  if (returnTo) window.location.hash = returnTo;

  return store(tokens.id_token, tokens.expires_in, tokens.refresh_token);
}

async function refresh(): Promise<TokenSet | null> {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return null;

  const response = await postToken({ grant_type: "refresh_token", refresh_token: refreshToken });
  if (!response.ok) {
    // Expired or revoked: not an error, just a sign-in that has run out.
    localStorage.removeItem(REFRESH_KEY);
    return null;
  }
  const tokens = await response.json();
  return store(tokens.id_token, tokens.expires_in);
}

/**
 * The session, if there is one: completing a redirect, or resuming from a refresh token.
 * Returns null when the user needs to sign in.
 */
export async function resume(): Promise<TokenSet | null> {
  const completed = await completeSignIn();
  if (completed) return completed;
  return refresh();
}

/** A valid ID token for the API, refreshing first if this one is about to expire. */
export async function idToken(): Promise<string | null> {
  if (current && current.expiresAt - REFRESH_MARGIN_MS > Date.now()) return current.idToken;
  // One refresh at a time, however many requests are waiting on it.
  inFlight ??= refresh().finally(() => {
    inFlight = null;
  });
  const refreshed = await inFlight;
  return refreshed?.idToken ?? null;
}

export function currentUser(): string | null {
  return current?.email ?? null;
}
